import Layout from "@/layouts/layout";
import Panel from "@/components/ui/Panel";
import Button, { ButtonLink } from "@/components/ui/Button";
import { Field, Label, Input, Textarea, Select } from "@/components/ui/Field";
import { useForm } from "@inertiajs/react";
import { useState } from "react";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { Submission } from "@/interfaces/submission";

// One of the cities you can hand in to, as the picker needs it.
// A city that never filled in its name renders as an empty option otherwise,
// which is an invisible row in the one dropdown the whole flow depends on.
// `??` does not catch it: the column holds an empty string, not null.
function cityLabel(e: { city: string | null; name: string }) {
  return e.city?.trim() || e.name;
}

function optionLabel(e: PickableEvent) {
  const where = [cityLabel(e), e.country?.trim()].filter(Boolean).join(", ");
  return e.starts_at_label ? `${where} - ${e.starts_at_label}` : where;
}

export type PickableEvent = {
  id: number;
  slug: string | null;
  name: string;
  city: string | null;
  country: string | null;
  starts_at_label: string | null;
  funds_by_sites: boolean;
  prefunding_deadline_label: string | null;
};

// Handing in the website you built, on one page.
//
// No account, no emailed code, no steps. Pick your city, say what you made,
// tell us where to post things. The code screens this replaced proved the
// address belonged to somebody on the roster, which was worth having when the
// roster was what got paid; it isn't worth losing most of the hand-ins for now
// that the hand-in is the thing being paid for.
//
// Coming back to change it happens through a link we email, so there is still
// nothing to remember and nothing to log into.
export default function SubmitSite({
  event,
  events,
  submissions_open,
  submission,
  code,
  min_age,
  max_age,
}: {
  event: SatelliteEvent | null;
  events: PickableEvent[];
  submissions_open: boolean;
  submission: Submission | null;
  code: { html: string | null; css: string | null } | null;
  min_age: number;
  max_age: number;
}) {
  const editing = submission !== null;

  if (!submissions_open && !editing) {
    return (
      <Layout>
        <div className="mx-auto flex w-full max-w-lg flex-col gap-6 py-8">
          <Panel title="not-yet.txt">
            <div className="flex flex-col gap-4">
              <h1 className="font-display text-heading text-ink leading-tight font-bold">
                Hand-ins aren't open
              </h1>
              <p className="text-body text-ink-muted">
                {event
                  ? "This one isn't taking sites right now. Your organizer will say when it is."
                  : "Nothing is taking sites right now. Check back soon."}
              </p>
              <ButtonLink
                href={event ? `/events/${event.slug ?? event.id}` : "/events"}
              >
                {event ? "Back to the event" : "See the cities"}
              </ButtonLink>
            </div>
          </Panel>
        </div>
      </Layout>
    );
  }

  if (!editing && events.length === 0 && !event) {
    return (
      <Layout>
        <div className="mx-auto flex w-full max-w-lg flex-col gap-6 py-8">
          <Panel title="not-yet.txt">
            <div className="flex flex-col gap-4">
              <h1 className="font-display text-heading text-ink leading-tight font-bold">
                No cities are taking sites yet
              </h1>
              <p className="text-body text-ink-muted">
                Check back once your city has a date.
              </p>
              <ButtonLink href="/events">See the cities</ButtonLink>
            </div>
          </Panel>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 py-8">
        {submission?.review_state === "approved" && submission.edit_token && (
          <GithubLinks submission={submission} />
        )}
        <SubmitForm
          event={event}
          events={events}
          submission={submission}
          code={code}
          minAge={min_age}
          maxAge={max_age}
        />
      </div>
    </Layout>
  );
}

function SubmitForm({
  event,
  events,
  submission,
  code,
  minAge,
  maxAge,
}: {
  event: SatelliteEvent | null;
  events: PickableEvent[];
  submission: Submission | null;
  code: { html: string | null; css: string | null } | null;
  minAge: number;
  maxAge: number;
}) {
  const editing = submission !== null;
  // Both columns can hold something, so the toggle decides what actually gets
  // sent. Otherwise switching from one to the other leaves the old answer
  // behind and the site quietly stays wherever it already was.
  const [mode, setMode] = useState<"paste" | "link">(
    editing && !submission.hosted ? "link" : "paste",
  );

  const { data, setData, post, patch, transform, processing, errors } =
    useForm<{
      event_id: string;
      title: string;
      url: string;
      description: string;
      screenshot: File | null;
      html: string;
      css: string;
      first_name: string;
      last_name: string;
      attendee_email: string;
      birthday: string;
      address_line_1: string;
      address_line_2: string;
      address_city: string;
      address_state: string;
      address_zip: string;
      address_country: string;
      repo_url: string;
      pages_url: string;
    }>({
      // Preselected when they came from a city's own link, and locked to that
      // city's id so the picker can't send the hours somewhere else by accident.
      event_id: event ? String(event.id) : "",
      title: submission?.title ?? "",
      url: submission?.url ?? "",
      description: submission?.description ?? "",
      screenshot: null,
      html: code?.html ?? "",
      css: code?.css ?? "",
      first_name: submission?.first_name ?? "",
      last_name: submission?.last_name ?? "",
      attendee_email: submission?.attendee_email ?? "",
      birthday: submission?.birthday ?? "",
      address_line_1: submission?.address_line_1 ?? "",
      address_line_2: submission?.address_line_2 ?? "",
      address_city: submission?.address_city ?? "",
      address_state: submission?.address_state ?? "",
      address_zip: submission?.address_zip ?? "",
      address_country: submission?.address_country ?? "",
      repo_url: submission?.repo_url ?? submission?.github_code_url ?? "",
      pages_url: submission?.pages_url ?? "",
    });

  // Whichever city is selected, for the deadline line under the picker.
  const chosen = event
    ? (events.find((e) => e.id === event.id) ?? null)
    : (events.find((e) => String(e.id) === data.event_id) ?? null);

  const sentBack = editing && submission.review_state === "rejected";

  return (
    <Panel
      title={editing ? "change-what-i-made.txt" : "hand-in-what-i-made.txt"}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          // Not setData: it lands after this handler, so the request would
          // carry whichever answer they just switched away from.
          transform((d) => ({
            ...d,
            url: mode === "paste" ? "" : d.url,
            html: mode === "paste" ? d.html : "",
            css: mode === "paste" ? d.css : "",
          }));

          if (editing && submission.edit_token) {
            patch(`/hand-in/${submission.edit_token}`);
          } else if (event) {
            post(`/events/${event.slug ?? event.id}/submit`);
          } else {
            post("/submit");
          }
        }}
      >
        <h1 className="font-display text-heading text-ink leading-tight font-bold">
          {editing ? "Change what you handed in" : "What did you make?"}
        </h1>

        {sentBack && (
          <div className="border-line bg-surface-alt border-l-4 py-2 pl-3">
            <p className="text-body text-ink font-bold">
              This came back for another go.
            </p>
            {submission.review_notes && (
              <p className="text-body text-ink-muted whitespace-pre-line">
                {submission.review_notes}
              </p>
            )}
            <p className="text-caption text-ink-soft mt-1">
              Fix it below and save. It goes straight back to a reviewer.
            </p>
          </div>
        )}

        {editing && submission.review_state === "pending" && (
          <p className="text-caption text-ink-soft">
            Someone's looking at this now. Saving changes sends it back to the
            start of the queue.
          </p>
        )}
        {editing && submission.review_state === "approved" && (
          <p className="text-caption text-ink-soft">
            This one's approved and up on the wall. Saving changes means someone
            has to look at it again.
          </p>
        )}

        {/* The city picker. First thing on the form, because everything below
            it is priced and deadlined by which one they choose. Locked once
            they arrived from a city's own link. */}
        {!editing &&
          (event ? (
            <Field label="Which Boba Bash?">
              <Input value={event.name} readOnly disabled />
            </Field>
          ) : (
            <Field
              label="Which Boba Bash?"
              hint="The city you built this for."
              error={errors.event_id}
            >
              <Select
                name="event_id"
                required
                value={data.event_id}
                onChange={(e) => setData("event_id", e.target.value)}
              >
                <option value="">Pick your city…</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {optionLabel(e)}
                  </option>
                ))}
              </Select>
            </Field>
          ))}

        {/* The one deadline that costs a city money if it's missed. */}
        {chosen?.funds_by_sites && chosen.prefunding_deadline_label && (
          <p className="text-caption text-ink-muted">
            Get this in before {cityLabel(chosen)} starts on{" "}
            <span className="text-ink font-bold">
              {chosen.prefunding_deadline_label}
            </span>
            . Someone looks at every site before it goes on the wall.
          </p>
        )}

        <Field label="What is it?" error={errors.title}>
          <Input
            name="title"
            required
            maxLength={120}
            placeholder="My cat fan page"
            value={data.title}
            onChange={(e) => setData("title", e.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-2">
          <Label hint="Either one is fine. Pasting is easiest if you haven't put it online.">
            Where's the site?
          </Label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === "paste" ? "brand" : "secondary"}
              onClick={() => setMode("paste")}
            >
              Paste my code
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === "link" ? "brand" : "secondary"}
              onClick={() => setMode("link")}
            >
              I've got a link
            </Button>
          </div>
        </div>

        {mode === "paste" ? (
          <>
            <Field
              label="Your HTML"
              hint="The whole of your index.html. We'll give it its own address you can share."
              error={errors.html}
            >
              <Textarea
                name="html"
                rows={10}
                required
                spellCheck={false}
                placeholder={"<h1>My cat</h1>\n<p>She is the best cat.</p>"}
                className="text-caption font-mono"
                value={data.html}
                onChange={(e) => setData("html", e.target.value)}
              />
            </Field>

            <Field
              label="Your CSS"
              hint="Optional. Only if it's in a separate file - if it's already in a <style> tag, leave this blank."
              error={errors.css}
            >
              <Textarea
                name="css"
                rows={6}
                spellCheck={false}
                placeholder={"body { background: #EFE8DB; }"}
                className="text-caption font-mono"
                value={data.css}
                onChange={(e) => setData("css", e.target.value)}
              />
            </Field>

            <p className="text-caption text-ink-soft">
              Scripts don't run on hosted pages - it's HTML and CSS only. If you
              need JavaScript, put it on GitHub Pages or Neocities and use the
              link option instead.
            </p>
          </>
        ) : (
          <Field
            label="Link to it"
            hint="Starts with https://. GitHub Pages, Neocities, Glitch, anywhere that gives you a URL."
            error={errors.url}
          >
            <Input
              name="url"
              type="url"
              inputMode="url"
              required
              placeholder="https://mycat.neocities.org"
              value={data.url}
              onChange={(e) => setData("url", e.target.value)}
            />
          </Field>
        )}

        <div className="border-line mt-2 border-t-2 pt-4">
          <h2 className="font-display text-subheading text-ink font-bold">
            {mode === "paste"
              ? "Put it on GitHub (optional)"
              : "Put it on GitHub"}
          </h2>
          <p className="text-caption text-ink-muted mt-1">
            {mode === "paste"
              ? "We keep the code you pasted on a page of its own, so you can skip this. If you want it on GitHub too, it takes about five minutes:"
              : "Every site needs its code on GitHub and a live link. It takes about five minutes:"}
          </p>
          <ol className="text-caption text-ink mt-2 flex list-decimal flex-col gap-1 pl-5">
            <li>
              Make a free account at{" "}
              <a
                className="text-link underline"
                href="https://github.com/signup"
                target="_blank"
                rel="noreferrer"
              >
                github.com
              </a>{" "}
              if you don't have one.
            </li>
            <li>
              Click <b>New repository</b>, name it something like{" "}
              <code>my-site</code>, make it <b>Public</b>.
            </li>
            <li>
              Click <b>uploading an existing file</b>, drop in your{" "}
              <code>index.html</code> (and <code>style.css</code>), then{" "}
              <b>Commit changes</b>.
            </li>
            <li>
              In <b>Settings &gt; Pages</b>, pick the <b>main</b> branch and
              save. Your site goes live at{" "}
              <code>https://you.github.io/my-site</code>.
            </li>
          </ol>
        </div>

        <Field
          label="GitHub repo"
          hint="Like https://github.com/you/my-site"
          error={errors.repo_url}
        >
          <Input
            name="repo_url"
            type="url"
            inputMode="url"
            required={mode === "link"}
            placeholder="https://github.com/you/my-site"
            value={data.repo_url}
            onChange={(e) => setData("repo_url", e.target.value)}
          />
        </Field>

        <Field
          label="Live site"
          hint="Your GitHub Pages link, like https://you.github.io/my-site"
          error={errors.pages_url}
        >
          <Input
            name="pages_url"
            type="url"
            inputMode="url"
            required={mode === "link"}
            placeholder="https://you.github.io/my-site"
            value={data.pages_url}
            onChange={(e) => setData("pages_url", e.target.value)}
          />
        </Field>

        <Field
          label="Screenshot"
          hint={
            submission?.screenshot_url
              ? "You've already added one. Pick a new file only to replace it. PNG, JPEG, WEBP or GIF, up to 5MB."
              : "A screenshot of your site. PNG, JPEG, WEBP or GIF, up to 5MB."
          }
          error={errors.screenshot}
        >
          <input
            type="file"
            required={!submission?.screenshot_url}
            name="screenshot"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="text-body text-ink font-body file:border-line file:bg-surface file:text-ink file:mr-3 file:cursor-pointer file:border-2 file:px-3 file:py-1.5 file:font-bold"
            onChange={(e) => setData("screenshot", e.target.files?.[0] ?? null)}
          />
        </Field>

        <Field
          label="Tell us about it"
          hint="What's it for, what was hard, what you're proud of."
          error={errors.description}
        >
          <Textarea
            name="description"
            required
            rows={4}
            maxLength={2000}
            value={data.description}
            onChange={(e) => setData("description", e.target.value)}
          />
        </Field>

        <div className="border-line mt-2 border-t-2 pt-4">
          <h2 className="font-display text-subheading text-ink font-bold">
            Who are you, and where do we send your stuff?
          </h2>
          <p className="text-caption text-ink-muted mt-1">
            You shipped, so we ship. This is only ever seen by Hack Club HQ -
            not your organizer, and not the gallery.
          </p>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Field label="First name" error={errors.first_name}>
              <Input
                name="first_name"
                autoComplete="given-name"
                required
                maxLength={80}
                value={data.first_name}
                onChange={(e) => setData("first_name", e.target.value)}
              />
            </Field>
          </div>
          <div className="flex-1">
            <Field label="Last name" error={errors.last_name}>
              <Input
                name="last_name"
                autoComplete="family-name"
                required
                maxLength={80}
                value={data.last_name}
                onChange={(e) => setData("last_name", e.target.value)}
              />
            </Field>
          </div>
        </div>

        <Field
          label="Email"
          hint="Where we send your verdict and the link to change this later. Use one you actually read."
          error={errors.attendee_email}
        >
          <Input
            name="attendee_email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            maxLength={200}
            value={data.attendee_email}
            onChange={(e) => setData("attendee_email", e.target.value)}
          />
        </Field>

        <Field
          label="Date of birth"
          hint={`Boba Bash is for ${minAge}-${maxAge} year olds.`}
          error={errors.birthday}
        >
          <Input
            type="date"
            name="birthday"
            autoComplete="bday"
            required
            value={data.birthday}
            onChange={(e) => setData("birthday", e.target.value)}
          />
        </Field>

        <Field label="Street address" error={errors.address_line_1}>
          <Input
            name="address_line_1"
            autoComplete="address-line1"
            required
            maxLength={200}
            value={data.address_line_1}
            onChange={(e) => setData("address_line_1", e.target.value)}
          />
        </Field>

        <Field
          label="Apartment, unit, etc."
          hint="Optional"
          error={errors.address_line_2}
        >
          <Input
            name="address_line_2"
            autoComplete="address-line2"
            maxLength={200}
            value={data.address_line_2}
            onChange={(e) => setData("address_line_2", e.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Field label="City" error={errors.address_city}>
              <Input
                name="address_city"
                autoComplete="address-level2"
                required
                maxLength={200}
                value={data.address_city}
                onChange={(e) => setData("address_city", e.target.value)}
              />
            </Field>
          </div>
          <div className="flex-1">
            <Field label="State or region" error={errors.address_state}>
              <Input
                name="address_state"
                autoComplete="address-level1"
                required
                maxLength={200}
                value={data.address_state}
                onChange={(e) => setData("address_state", e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Field label="ZIP or postcode" error={errors.address_zip}>
              <Input
                name="address_zip"
                autoComplete="postal-code"
                required
                maxLength={80}
                value={data.address_zip}
                onChange={(e) => setData("address_zip", e.target.value)}
              />
            </Field>
          </div>
          <div className="flex-1">
            <Field label="Country" error={errors.address_country}>
              <Input
                name="address_country"
                autoComplete="country-name"
                required
                maxLength={80}
                value={data.address_country}
                onChange={(e) => setData("address_country", e.target.value)}
              />
            </Field>
          </div>
        </div>

        {errors.base && (
          <p className="text-body text-sunset-orange font-bold">
            {errors.base}
          </p>
        )}

        <Button type="submit" disabled={processing} className="self-start">
          {processing ? "Sending…" : editing ? "Save changes" : "Hand it in"}
        </Button>
      </form>
    </Panel>
  );
}

// The GitHub repo and live link, asked for once a site is approved. Its own
// form and its own endpoint, because saving the main form re-opens review.
function GithubLinks({ submission }: { submission: Submission }) {
  const { data, setData, patch, processing, errors } = useForm({
    repo_url: submission.repo_url ?? submission.github_code_url ?? "",
    pages_url:
      submission.pages_url ??
      (submission.hosted ? "" : (submission.playable_url ?? "")),
  });
  const codeSaved = !submission.github_code_url && !!submission.code_page_url;
  const done =
    Boolean(submission.repo_url ?? submission.github_code_url) &&
    Boolean(
      submission.pages_url ?? (!submission.hosted && submission.playable_url),
    );

  return (
    <Panel title="github.txt">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          patch(`/hand-in/${submission.edit_token}/links`, {
            preserveScroll: true,
          });
        }}
      >
        <h2 className="font-display text-heading text-ink leading-tight font-bold">
          {done
            ? "Your GitHub links"
            : codeSaved
              ? "Want it on GitHub too?"
              : "One more step: put it on GitHub"}
        </h2>
        {codeSaved ? (
          <p className="text-body text-ink-muted">
            Your code is saved on{" "}
            <a
              className="text-link underline"
              href={submission.code_page_url ?? undefined}
              target="_blank"
              rel="noreferrer"
            >
              its own page
            </a>
            , so you're all set. If you'd like it on GitHub too, it takes about
            five minutes:
          </p>
        ) : (
          <p className="text-body text-ink-muted">
            Hack Club keeps a record of everything teenagers ship, and it needs
            your code on GitHub. Takes about five minutes:
          </p>
        )}
        <ol className="text-body text-ink flex list-decimal flex-col gap-1 pl-5">
          <li>
            Make a free account at{" "}
            <a
              className="text-link underline"
              href="https://github.com/signup"
              target="_blank"
              rel="noreferrer"
            >
              github.com
            </a>{" "}
            if you don't have one.
          </li>
          <li>
            Click <b>New repository</b>, name it something like{" "}
            <code>my-site</code>, make it <b>Public</b>.
          </li>
          <li>
            Click <b>uploading an existing file</b>, drop in your{" "}
            <code>index.html</code> (and <code>style.css</code>), then{" "}
            <b>Commit changes</b>. Your code is in the boxes further down.
          </li>
          <li>
            In <b>Settings &gt; Pages</b>, pick the <b>main</b> branch and save.
            Your site goes live at <code>https://you.github.io/my-site</code>.
          </li>
        </ol>

        <Field
          label="GitHub repo"
          hint="Like https://github.com/you/my-site"
          error={errors.repo_url}
        >
          <Input
            name="repo_url"
            type="url"
            inputMode="url"
            required={!codeSaved}
            placeholder="https://github.com/you/my-site"
            value={data.repo_url}
            onChange={(e) => setData("repo_url", e.target.value)}
          />
        </Field>

        <Field
          label="Live site"
          hint="Your GitHub Pages link, like https://you.github.io/my-site"
          error={errors.pages_url}
        >
          <Input
            name="pages_url"
            type="url"
            inputMode="url"
            required={!codeSaved}
            placeholder="https://you.github.io/my-site"
            value={data.pages_url}
            onChange={(e) => setData("pages_url", e.target.value)}
          />
        </Field>

        {submission.repo_check_error && (
          <p className="text-body text-sunset-orange font-bold">
            {submission.repo_check_error} Fix it and save again.
          </p>
        )}

        <p className="text-caption text-ink-soft">
          Saving these doesn't change your site or send it back for review.
        </p>

        <Button type="submit" disabled={processing} className="self-start">
          {processing ? "Saving…" : "Save my links"}
        </Button>
      </form>
    </Panel>
  );
}
